import type { Hydrant } from "./types.ts";
import { supabase } from "./supabase.ts";

export interface HydrantFeatureProps {
  id: string;
  type: string;
  diameter: number | null;
  dn: string;
  pressure: number | null;
  last_inspection: string | null;
  last_inspection_result: string | null;
  status: "ok" | "warn" | "bad" | "unknown";
  deleted: boolean;
}

export type HydrantFeatureCollection = GeoJSON.FeatureCollection<GeoJSON.Point, HydrantFeatureProps>;

// Anzeige-Typ: Sauganschlüsse (Saugbetrieb bzw. emergency=suction_point) bekommen
// eine eigene Kategorie, unabhängig vom OSM-Bautyp (z. B. pipe).
export function displayTypeFor(hydrantType: string | null, tags: Record<string, string>): string {
  if (hydrantType === "pond") return "pond";
  if (tags["fire_hydrant:pressure"] === "suction" || tags.emergency === "suction_point") {
    return "suction";
  }
  return hydrantType ?? "other";
}

function statusFor(lastResult: string | null, lastInspection: string | null): HydrantFeatureProps["status"] {
  if (!lastResult) return "unknown";
  if (lastResult === "defect" || lastResult === "missing") return "bad";
  if (lastResult === "blocked" || lastResult === "maengel") return "warn";
  if (!lastInspection) return "unknown";
  const days = (Date.now() - new Date(lastInspection).getTime()) / (1000 * 60 * 60 * 24);
  if (days > 365) return "warn";
  return "ok";
}

export async function loadHydrants(): Promise<{
  hydrants: Hydrant[];
  features: HydrantFeatureCollection;
}> {
  const { data, error } = await supabase
    .from("hydrants_with_latest_inspection")
    .select("*");

  if (error) throw error;

  const hydrants: Hydrant[] = (data ?? []).map((row) => ({
    id: row.id,
    osm_type: row.osm_type,
    osm_id: row.osm_id,
    lon: row.lon,
    lat: row.lat,
    hydrant_type: row.hydrant_type,
    diameter_mm: row.diameter_mm,
    pressure_bar: row.pressure_bar,
    flow_capacity_lpm: row.flow_capacity_lpm,
    position: row.position_tag,
    ref: row.ref,
    colour: row.colour,
    last_osm_change: row.last_osm_change,
    tags: row.tags ?? {},
    deleted_at: row.deleted_at ?? null,
    replaced_by: row.replaced_by ?? null,
  }));

  const features: HydrantFeatureCollection = {
    type: "FeatureCollection",
    features: (data ?? []).map((row) => ({
      type: "Feature",
      geometry: { type: "Point", coordinates: [row.lon, row.lat] },
      properties: {
        id: row.id,
        type: displayTypeFor(row.hydrant_type, row.tags ?? {}),
        diameter: row.diameter_mm,
        dn: row.diameter_mm != null ? String(row.diameter_mm) : "",
        pressure: row.pressure_bar,
        last_inspection: row.last_inspection_at ?? null,
        last_inspection_result: row.last_inspection_result ?? null,
        status: statusFor(row.last_inspection_result ?? null, row.last_inspection_at ?? null),
        deleted: row.deleted_at != null,
      },
    })),
  };

  return { hydrants, features };
}
